import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { snippets, tags } from '../api';
import type {
  LibraryScope,
  SnippetListResponse,
  SnippetResponse,
  SnippetKindFilter,
  SnippetSort,
  TagResponse,
} from '../api';

export interface LibraryFilters {
  q?: string;
  tag?: string[];
  language?: string;
  isFavorite?: boolean;
  isArchived?: boolean;
  deleted?: boolean;
  sort?: SnippetSort;
  scope?: LibraryScope;
  teamId?: string;
  /**
   * Which kind of library item to list. Scripts and diagrams share a table but not a list, and the
   * API defaults to Script — so leaving this off is the scripts library, not everything.
   */
  kind?: SnippetKindFilter;
}

export const snippetsKey = (filters: LibraryFilters) => ['snippets', filters] as const;
export const snippetKey = (id: string) => ['snippet', id] as const;
export const tagsKey = ['tags'] as const;

export function useSnippets(filters: LibraryFilters) {
  return useQuery({
    queryKey: snippetsKey(filters),
    queryFn: async (): Promise<SnippetListResponse> => {
      const { data, error } = await snippets.list({ query: filters });
      if (error) {
        throw error;
      }
      return data ?? { items: [], total: 0 };
    },
  });
}

export function useSnippet(id: string | null) {
  return useQuery({
    queryKey: snippetKey(id ?? ''),
    enabled: id !== null,
    queryFn: async (): Promise<SnippetResponse> => {
      const { data, error } = await snippets.get({ path: { id: id! } });
      if (error) {
        throw error;
      }
      if (!data) {
        throw new Error('That snippet could not be loaded.');
      }
      return data;
    },
  });
}

/**
 * Tags for the sidebar. With a team id, just that team's; without, every library the caller can
 * reach — which is what the tag input wants, since a snippet's tag may come from either.
 */
/**
 * Tags for one library, counted for one kind. The count decides what the sidebar shows beside each
 * tag, and a diagrams sidebar reading "bash (7)" where all seven are scripts sends people to an
 * empty list.
 */
export function useTags(teamId?: string, kind: SnippetKindFilter = 'Script') {
  return useQuery({
    queryKey: [...tagsKey, teamId ?? null, kind],
    queryFn: async (): Promise<TagResponse[]> => {
      const { data, error } = await tags.list({
        query: { ...(teamId ? { teamId } : {}), kind },
      });
      if (error) {
        throw error;
      }
      return data ?? [];
    },
  });
}

/** Invalidates every snippet list, whatever filters it was fetched with. */
function invalidateLists(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['snippets'] });
}

/**
 * A tag's name and colour are denormalised into every snippet payload as a `TagSummary`, so
 * editing one is not just a change to the tags query: the chips on the open snippet and the pills
 * in the list carry their own copies. Invalidating only `tagsKey` recolours the sidebar and
 * leaves every other appearance of the tag stale.
 */
function invalidateTagsEverywhere(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: tagsKey });
  void queryClient.invalidateQueries({ queryKey: ['snippet'] });
  invalidateLists(queryClient);
}

export function useCreateSnippet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { title: string; description?: string | null; teamId?: string }) => {
      const { data, error } = await snippets.create({
        // The contract asks for these explicitly rather than by omission.
        body: {
          title: input.title,
          description: input.description ?? null,
          fragments: null,
          teamId: input.teamId ?? null,
        },
      });
      if (error) {
        throw error;
      }
      return data!;
    },
    onSuccess: (snippet) => {
      queryClient.setQueryData(snippetKey(snippet.id), snippet);
      invalidateLists(queryClient);
    },
  });
}

export function useUpdateSnippet(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { title: string; description?: string | null }) => {
      const { data, error } = await snippets.update({
        path: { id },
        body: { title: input.title, description: input.description ?? null },
      });
      if (error) {
        throw error;
      }
      return data!;
    },
    onSuccess: (snippet) => {
      queryClient.setQueryData(snippetKey(id), snippet);
      invalidateLists(queryClient);
    },
  });
}

/**
 * Favouriting is optimistic: the star flips immediately and rolls back if the request fails.
 * Waiting for a round trip to acknowledge a toggle feels broken.
 */
export function useToggleFavorite(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (value: boolean) => {
      const { data, error } = await snippets.setFavorite({ path: { id }, body: { value } });
      if (error) {
        throw error;
      }
      return data!;
    },
    onMutate: async (value) => {
      await queryClient.cancelQueries({ queryKey: snippetKey(id) });
      const previous = queryClient.getQueryData<SnippetResponse>(snippetKey(id));

      if (previous) {
        queryClient.setQueryData(snippetKey(id), { ...previous, isFavorite: value });
      }

      return { previous };
    },
    onError: (_error, _value, context) => {
      if (context?.previous) {
        queryClient.setQueryData(snippetKey(id), context.previous);
      }
    },
    onSettled: () => invalidateLists(queryClient),
  });
}

export function useSetArchived(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (value: boolean) => {
      const { error } = await snippets.setArchived({ path: { id }, body: { value } });
      if (error) {
        throw error;
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: snippetKey(id) });
      invalidateLists(queryClient);
    },
  });
}

export function useDeleteSnippet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await snippets.remove({ path: { id } });
      if (error) {
        throw error;
      }

      return id;
    },
    onSuccess: (id) => {
      // The cached copy still says it is live. Opening it from the trash inside the stale window
      // would otherwise show an editable snippet with Share and Delete on it.
      void queryClient.invalidateQueries({ queryKey: snippetKey(id) });
      invalidateLists(queryClient);
    },
  });
}

/** Removes a trashed snippet for good. There is no undo behind this one. */
export function usePurgeSnippet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await snippets.purge({ path: { id } });
      if (error) {
        throw error;
      }

      return id;
    },
    onSuccess: (id) => {
      // Drop it rather than refetching: there is nothing left to fetch.
      queryClient.removeQueries({ queryKey: snippetKey(id) });
      invalidateLists(queryClient);
    },
  });
}

export function useRestoreSnippet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await snippets.restore({ path: { id } });
      if (error) {
        throw error;
      }

      return id;
    },
    onSuccess: (id) => {
      // The open snippet still says it is in the trash until this refetches.
      void queryClient.invalidateQueries({ queryKey: snippetKey(id) });
      invalidateLists(queryClient);
    },
  });
}

export function useAddFragment(snippetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: { name: string; language: string; content: string }) => {
      const { data, error } = await snippets.addFragment({ path: { id: snippetId }, body });
      if (error) {
        throw error;
      }
      return data!;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: snippetKey(snippetId) });
      invalidateLists(queryClient);
    },
  });
}

export function useUpdateFragment(snippetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      fragmentId: string;
      name: string;
      language: string;
      content: string;
    }) => {
      const { error } = await snippets.updateFragment({
        path: { id: snippetId, fragmentId: input.fragmentId },
        body: { name: input.name, language: input.language, content: input.content },
      });
      if (error) {
        throw error;
      }

      return input;
    },
    onSuccess: (saved) => {
      // Patch the cached snippet rather than invalidating it. We know exactly what was written,
      // so a refetch would only risk landing mid-keystroke and showing a stale fragment name in
      // the tabs.
      queryClient.setQueryData<SnippetResponse>(snippetKey(snippetId), (current) =>
        current
          ? {
              ...current,
              fragments: current.fragments.map((fragment) =>
                fragment.id === saved.fragmentId
                  ? {
                      ...fragment,
                      name: saved.name,
                      language: saved.language,
                      content: saved.content,
                    }
                  : fragment,
              ),
            }
          : current,
      );

      // The list shows languages and the updated time, both of which may have changed.
      invalidateLists(queryClient);
    },
  });
}

export function useRemoveFragment(snippetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fragmentId: string) => {
      const { error } = await snippets.removeFragment({
        path: { id: snippetId, fragmentId },
      });
      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: snippetKey(snippetId) });
      invalidateLists(queryClient);
    },
  });
}

/** Attaching is optimistic; the tag appears at once and is reconciled when the server answers. */
export function useAttachTag(snippetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await snippets.attachTag({
        path: { id: snippetId },
        body: { name },
      });
      if (error) {
        throw error;
      }
      return data!;
    },
    onMutate: async (name) => {
      await queryClient.cancelQueries({ queryKey: snippetKey(snippetId) });
      const previous = queryClient.getQueryData<SnippetResponse>(snippetKey(snippetId));

      if (previous) {
        queryClient.setQueryData(snippetKey(snippetId), {
          ...previous,
          // A placeholder id, replaced by the real tag on success.
          tags: [...previous.tags, { id: `pending:${name}`, name, color: null }],
        });
      }

      return { previous };
    },
    onError: (_error, _name, context) => {
      if (context?.previous) {
        queryClient.setQueryData(snippetKey(snippetId), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: snippetKey(snippetId) });
      void queryClient.invalidateQueries({ queryKey: tagsKey });
      invalidateLists(queryClient);
    },
  });
}

/**
 * Renaming and recolouring are the same request, because the API takes both fields — passing the
 * current value for the one you are not changing is the contract, not a shortcut.
 *
 * The optimistic patch covers the tags queries only. That is where the click happened, so the
 * swatch and the sidebar dot answer immediately; everywhere else the tag appears is reconciled by
 * the invalidation once the server has agreed.
 */
export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { id: string; name: string; color: string | null }) => {
      const { data, error } = await tags.update({
        path: { id: input.id },
        body: { name: input.name, color: input.color },
      });
      if (error) {
        throw error;
      }
      return data!;
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: tagsKey });

      // Every tags query, whatever library it was fetched for: the same tag can sit in more than
      // one of them.
      const previous = queryClient.getQueriesData<TagResponse[]>({ queryKey: tagsKey });

      queryClient.setQueriesData<TagResponse[]>({ queryKey: tagsKey }, (current) =>
        current?.map((tag) =>
          tag.id === input.id ? { ...tag, name: input.name, color: input.color } : tag,
        ),
      );

      return { previous };
    },
    onError: (_error, _input, context) => {
      for (const [key, value] of context?.previous ?? []) {
        queryClient.setQueryData(key, value);
      }
    },
    onSettled: () => invalidateTagsEverywhere(queryClient),
  });
}

/** Deleting a tag detaches it from every snippet it was on, so this waits rather than guessing. */
export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await tags.remove({ path: { id } });
      if (error) {
        throw error;
      }
      return id;
    },
    onSuccess: () => invalidateTagsEverywhere(queryClient),
  });
}

export function useDetachTag(snippetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tagId: string) => {
      const { error } = await snippets.detachTag({ path: { id: snippetId, tagId } });
      if (error) {
        throw error;
      }
    },
    onMutate: async (tagId) => {
      await queryClient.cancelQueries({ queryKey: snippetKey(snippetId) });
      const previous = queryClient.getQueryData<SnippetResponse>(snippetKey(snippetId));

      if (previous) {
        queryClient.setQueryData(snippetKey(snippetId), {
          ...previous,
          tags: previous.tags.filter((tag) => tag.id !== tagId),
        });
      }

      return { previous };
    },
    onError: (_error, _tagId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(snippetKey(snippetId), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: snippetKey(snippetId) });
      void queryClient.invalidateQueries({ queryKey: tagsKey });
      invalidateLists(queryClient);
    },
  });
}
